import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './LandingPage.module.css';

// Uncomment these imports once images are added to src/images/
// import logoBleu from '../../images/logos/logo-bleu.png';
// import logoBlanc from '../../images/logos/logo-blanc.png';
 import appHero from '../../images/landing/app-hero.png';
 import appSolution from '../../images/landing/app-solution.png';

const CONTACT_URL = `${process.env.REACT_APP_BASE_URL}/api/contact`;

const r = (envKey, fallback) => parseFloat(process.env[envKey]) || fallback;

const TIERS = [
  { min: 1,   max: 19,       price: r('REACT_APP_RATE_SEASONAL_BASE', 3.00), get label() { return `1–19 · ${this.price.toFixed(2).replace('.', ',')}€`; } },
  { min: 20,  max: 49,       price: r('REACT_APP_RATE_SEASONAL_20',   2.90), get label() { return `20–49 · ${this.price.toFixed(2).replace('.', ',')}€`; } },
  { min: 50,  max: 99,       price: r('REACT_APP_RATE_SEASONAL_50',   2.80), get label() { return `50–99 · ${this.price.toFixed(2).replace('.', ',')}€`; } },
  { min: 100, max: 199,      price: r('REACT_APP_RATE_SEASONAL_100',  2.70), get label() { return `100–199 · ${this.price.toFixed(2).replace('.', ',')}€`; } },
  { min: 200, max: Infinity, price: r('REACT_APP_RATE_SEASONAL_200',  2.50), get label() { return `200+ · ${this.price.toFixed(2).replace('.', ',')}€`; } },
];

function calcMonthly(n) {
  const tier = getLastTier(n);
  if (!tier?.price) return null;
  return n * tier.price;
}

function getLastTier(n) {
  return TIERS.find(t => n <= t.max);
}

function fmt(n) {
  return n % 1 === 0 ? n + '€' : n.toFixed(2).replace('.', ',') + '€';
}

const c = (...names) => names.filter(Boolean).map(n => styles[n]).filter(Boolean).join(' ');

export default function LandingPage() {
  const [burgerOpen, setBurgerOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [simCount, setSimCount] = useState(10);
  const [simPlanType, setSimPlanType] = useState('seasonal');
  const [formState, setFormState] = useState('idle');
  const permanentRate = r('REACT_APP_RATE_PERMANENT', 3.00);
  const landingRef = useRef(null);

  const closeMobile = () => setBurgerOpen(false);

  const scrollToCta = (e) => {
    e.preventDefault();
    setBurgerOpen(false);
    const el = document.getElementById('cta');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => {
      el.classList.add(styles.ctaFlash);
      el.addEventListener('animationend', () => el.classList.remove(styles.ctaFlash), { once: true });
    }, 700);
  };

  useEffect(() => {
    document.body.style.overflow = burgerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [burgerOpen]);

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          const siblings = Array.from(
            e.target.parentElement.getElementsByClassName(styles.reveal)
          ).filter(el => !el.classList.contains(styles.revealVisible));
          const idx = siblings.indexOf(e.target);
          setTimeout(() => e.target.classList.add(styles.revealVisible), idx * 80);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -30px 0px' });

    const elements = landingRef.current ? Array.from(landingRef.current.getElementsByClassName(styles.reveal)) : [];
    elements.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const currentTier = getLastTier(simCount);
  const monthly = currentTier?.price ? calcMonthly(simCount) : null;
  const seasonTotal = monthly ? monthly * 3 : null;
  const effectiveRate = monthly ? monthly / simCount : null;
  const perMonthSeason = seasonTotal ? seasonTotal / 3 / simCount : null;
  const permanentMonthly = simCount * permanentRate;
  const permanentAnnual = permanentMonthly * 12;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormState('loading');
    const data = Object.fromEntries(new FormData(e.target));
    try {
      const res = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
      });
      setFormState(res.ok ? 'success' : 'error');
    } catch {
      setFormState('error');
    }
  };

  return (
    <div className={styles.landingPage} ref={landingRef}>

      {/* NAV */}
      <nav className={styles.nav}>
        <div className={styles.navLogo}>
          {/* <img src={logoBleu} alt="Behandy" /> */}
          <span style={{ fontFamily: 'Urbanist, sans-serif', fontWeight: 800, fontSize: 24, color: 'var(--teal)' }}>Behandy</span>
        </div>
        <ul className={styles.navLinks}>
          <li><a href="#probleme">Le problème</a></li>
          <li><a href="#solution">La solution</a></li>
          <li><a href="#comment">Comment ça marche</a></li>
          <li><a href="#tarif">Tarif</a></li>
          <li><a href="#faq">FAQ</a></li>
        </ul>
        <div className={styles.navRight}>
          <Link to="/login" className={styles.navLogin}>Se connecter</Link>
          <a href="#cta" className={styles.navCta} onClick={scrollToCta}>Activer pour mon équipe →</a>
          <button
            className={c('burger', burgerOpen ? 'burgerOpen' : null)}
            aria-label="Menu"
            onClick={() => setBurgerOpen(o => !o)}
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* MOBILE NAV */}
      <div className={c('mobileNav', burgerOpen ? 'mobileNavOpen' : null)}>
        <a href="#probleme" onClick={closeMobile}>Le problème</a>
        <a href="#solution" onClick={closeMobile}>La solution</a>
        <a href="#comment" onClick={closeMobile}>Comment ça marche</a>
        <a href="#tarif" onClick={closeMobile}>Tarif</a>
        <a href="#faq" onClick={closeMobile}>FAQ</a>
        <Link to="/login" onClick={closeMobile}>Se connecter</Link>
        <a href="#cta" className={styles.mobileCta} onClick={scrollToCta}>Activer pour mon équipe →</a>
      </div>

      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.heroBgShapes}>
          <div className={c('shape', 'shape1')} />
          <div className={c('shape', 'shape2')} />
          <div className={c('shape', 'shape3')} />
        </div>
        <div className={styles.heroGrid}>
          <div className={styles.heroContent}>
            <div className={styles.heroBadge}>
              <div className={styles.badgeDot} /> Le CSE pensé pour les saisonniers
            </div>
            <h1 className={styles.heroH1}>
              Fidélisez vos saisonniers. Offrez-leur une saison <span className={styles.accent}>inoubliable.</span>
            </h1>
            <p className={styles.heroSub}>
              Behandy est la plateforme d'avantages qui améliore le quotidien de vos saisonniers —<br />
              <strong>Simple à activer. Zéro gestion. Impact immédiat.</strong>
            </p>
            <div className={styles.heroActions}>
              <a href="#cta" className={styles.btnPrimary} onClick={scrollToCta}>
                Offrir Behandy à mes équipes
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
            <div className={c('heroSocialProof', 'reveal')}>
              <div className={styles.proofAvatar}>D</div>
              <span>Déjà adopté par <strong style={{ color: 'var(--text)' }}>VVF</strong> — rejoignez les premiers établissements early adopters.</span>
            </div>
          </div>
          <div className={styles.heroVisual}>
             <img src={appHero} alt="Application Behandy" /> 
          </div>
        </div>
      </section>

      <ProblemSection styles={styles} c={c} />
      <WhySection styles={styles} c={c} />
      <SolutionSection styles={styles} c={c} />
      <CategoriesSection styles={styles} c={c} />
      <HowSection styles={styles} c={c} />
      <BenefitsSection styles={styles} c={c} />
      <TestimonialSection styles={styles} c={c} />
      <VisionSection styles={styles} c={c} />
      <PricingSection
        styles={styles} c={c}
        simCount={simCount} setSimCount={setSimCount}
        simPlanType={simPlanType} setSimPlanType={setSimPlanType}
        currentTier={currentTier} monthly={monthly}
        seasonTotal={seasonTotal} effectiveRate={effectiveRate}
        perMonthSeason={perMonthSeason} fmt={fmt} TIERS={TIERS}
        baseRate={TIERS[0].price}
        permanentRate={permanentRate}
        permanentMonthly={permanentMonthly}
        permanentAnnual={permanentAnnual}
        scrollToCta={scrollToCta}
      />
      <FaqSection styles={styles} c={c} openFaq={openFaq} setOpenFaq={setOpenFaq} />
      <CtaSection styles={styles} c={c} formState={formState} handleFormSubmit={handleFormSubmit} />
      <FooterSection styles={styles} c={c} />

    </div>
  );
}

// ── Section components below ──

function ProblemSection({ styles, c }) {
  return (
    <section className={styles.problem} id="probleme">
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <span className={c('sectionTag', 'reveal')}>Le problème</span>
          <h2 className={c('sectionTitle', 'reveal')}>Chaque saison, vous repartez de zéro.</h2>
          <p className={c('sectionSub', 'reveal')}>Recruter, former, intégrer. Le turnover saisonnier coûte cher en temps, en argent, en énergie. Et chaque saison, vous recommencez à zéro.</p>
        </div>
        <div className={styles.problemLayout}>
          <div className={styles.painList}>
            {[
              { icon: '🔄', title: 'Un recrutement interminable chaque saison', text: 'Trouver, sélectionner, former de nouvelles équipes — en boucle, saison après saison.' },
              { icon: '🏃', title: 'Les saisonniers ne reviennent pas', text: "Un sur deux change d'employeur la saison suivante. Souvent par manque de différenciation." },
              { icon: '⚖️', title: "Des offres d'emploi identiques", text: 'Même salaire, mêmes horaires. Comment se démarquer quand tout se ressemble ?' },
              { icon: '🚫', title: 'Aucun avantage adapté aux contrats courts', text: 'Les CSE traditionnels sont conçus pour les CDI. Les saisonniers en sont structurellement exclus.' },
            ].map((p, i) => (
              <div key={p.icon} className={c('painCard', 'reveal')}>
                <div className={styles.painIcon}>{p.icon}</div>
                <div><strong>{p.title}</strong><p>{p.text}</p></div>
              </div>
            ))}
          </div>
          <div className={styles.statsStack}>
            {[
              { mod: 'bigStatTeal', num: '+50%', text: "de taux de turnover dans l'hôtellerie-restauration, contre 15% en moyenne tous secteurs confondus", source: '— INSEE / DARES' },
              { mod: 'bigStatYellow', num: '200k', text: "postes essentiellement saisonniers non pourvus chaque année dans l'HCR", source: "— UMIH / L'Echo Touristique 2025" },
              { mod: 'bigStatTeal', num: '59%', text: "des projets de recrutement dans l'HCR jugés difficiles à pourvoir", source: '— France Travail / Assemblée nationale 2025' },
            ].map((s, i) => (
              <div key={s.num} className={c('bigStat', s.mod, 'reveal')}>
                <div className={styles.bigStatNum}>{s.num}</div>
                <div className={styles.bigStatBody}>
                  <div className={styles.bigStatText}>{s.text}</div>
                  <div className={styles.bigStatSource}>{s.source}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhySection({ styles, c }) {
  return (
    <section className={styles.why} id="fidelisation">
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <span className={c('sectionTag', 'reveal')}>Pourquoi agir</span>
          <h2 className={c('sectionTitle', 'reveal')}>Fidéliser, c'est votre meilleur investissement RH.</h2>
          <p className={c('sectionSub', 'reveal')}>Un saisonnier qui revient, c'est du temps économisé, une équipe efficace dès le premier jour, et une ambiance de travail meilleure pour tous.</p>
        </div>
        <div className={styles.whyCards}>
          {[
            { icon: '💰', title: 'Réduisez les coûts cachés du turnover', text: "Recrutement, onboarding, perte de productivité pendant la prise de poste. Un saisonnier fidèle vous économise tout ça.", tag: '↓ Coût de recrutement' },
            { icon: '⚡', title: 'Opérationnels dès le Jour 1', text: 'Une équipe qui revient connaît vos process, vos standards et votre culture. Vous démarrez la saison à pleine vitesse.', tag: '↑ Performance opérationnelle' },
            { icon: '🏆', title: 'Gagnez la guerre du recrutement', text: "Les saisonniers choisissent entre plusieurs offres similaires. Un avantage concret peut être le facteur décisif qui les fait choisir votre établissement.", tag: '↑ Attractivité employeur' },
          ].map((w, i) => (
            <div key={w.icon} className={c('whyCard', 'reveal')}>
              <div className={styles.whyCardIcon}>{w.icon}</div>
              <h3>{w.title}</h3>
              <p>{w.text}</p>
              <span className={styles.whyTag}>{w.tag}</span>
            </div>
          ))}
        </div>
        <div className={styles.whyStats}>
          {[
            { num: '43%', label: "des salariés placent les avantages dans le top 3 de leurs critères de choix d'un employeur", source: '— Leeto' },
            { num: '92%', label: 'des salariés placent la qualité de vie au travail parmi leurs priorités', source: '— Baromètre QVT' },
          ].map((s, i) => (
            <div key={s.num} className={c('whyStat', 'reveal')}>
              <div className={styles.whyStatNum}>{s.num}</div>
              <div className={styles.whyStatLabel}>{s.label}</div>
              <div className={styles.whyStatSource}>{s.source}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SolutionSection({ styles, c }) {
  return (
    <section className={styles.solution} id="solution">
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <span className={c('sectionTag', 'reveal')}>La solution</span>
          <h2 className={c('sectionTitle', 'reveal')}>Behandy : les avantages salariés enfin adaptés aux saisonniers.</h2>
          <p className={c('sectionSub', 'reveal')}>Une app simple, un catalogue de réductions utiles, une activation en 3 minutes. Vos équipes en profitent dès leur premier jour.</p>
        </div>
        <div className={styles.solutionLayout}>
          <div className={c('solutionVisual', 'reveal')}>
             <img src={appSolution} alt="Application Behandy" /> 
          </div>
          <div className={styles.solutionPoints}>
            {[
              { num: '1', title: 'Un catalogue riche et utile', text: "Restaurants, loisirs, sorties, services du quotidien — des réductions concrètes qui améliorent le quotidien pendant la saison (et après !)." },
              { num: '2', title: 'Activation instantanée via code', text: "Vous partagez un code. Vos saisonniers téléchargent l'app et activent leurs avantages en moins de 2 minutes. Aucune démarche administrative." },
              { num: '3', title: 'Les avantages continuent après la saison', text: "Vos saisonniers gardent accès à l'app après la fin du contrat. Un geste fort qui crée un lien durable et les incite à revenir." },
              { num: '4', title: 'Zéro gestion pour vous', text: "Behandy s'occupe de tout. Pas de catalogue à mettre à jour, pas de bons à imprimer, pas d'administration supplémentaire." },
            ].map((p, i) => (
              <div key={p.num} className={c('solutionPoint', 'reveal')}>
                <div className={styles.pointNum}>{p.num}</div>
                <div><h4>{p.title}</h4><p>{p.text}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoriesSection({ styles, c }) {
  const cats = [
    { emoji: '🍔', name: 'Manger un bout', desc: 'Restaurants, snacks, coffee shops, fast-food...' },
    { emoji: '🍻', name: 'Boire un coup', desc: 'Bars, happy hours, sorties entre collègues...' },
    { emoji: '🎢', name: 'Activités', desc: 'Cinéma, escape game, karting, surf, sport...' },
    { emoji: '🛒', name: 'Quotidien', desc: 'Coiffeur, laverie, mobilité, services...' },
    { emoji: '🏝', name: 'Vacances', desc: 'Séjours, week-ends, activités touristiques...' },
  ];
  return (
    <section className={styles.categories}>
      <div className={styles.container}>
        <div className={styles.sectionHeader} style={{ textAlign: 'center' }}>
          <span className={c('sectionTag', 'reveal')}>Ce que vos saisonniers vont adorer</span>
          <h2 className={c('sectionTitle', 'reveal')}>Des avantages dans tous les domaines.</h2>
          <p className={c('sectionSub', 'reveal')} style={{ margin: '0 auto' }}>Offres locales, nationales et exclusives — accessibles partout, directement depuis leur téléphone.</p>
        </div>
        <div className={styles.catsGrid}>
          {cats.map((cat, i) => (
            <div key={cat.emoji} className={c('catBig', 'reveal')}>
              <span className={styles.catBigEmoji}>{cat.emoji}</span>
              <div className={styles.catBigName}>{cat.name}</div>
              <div className={styles.catBigDesc}>{cat.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSection({ styles, c }) {
  return (
    <section className={styles.how} id="comment">
      <div className={styles.container}>
        <div className={styles.sectionHeader} style={{ textAlign: 'center' }}>
          <span className={c('sectionTag', 'reveal')}>Mise en place</span>
          <h2 className={c('sectionTitle', 'reveal')}>Opérationnel en 3 minutes. Vraiment.</h2>
          <p className={c('sectionSub', 'reveal')} style={{ margin: '0 auto' }}>Pas de configuration complexe, pas de formation. Vous activez, vous partagez, c'est tout.</p>
        </div>
        <div className={styles.stepsRow}>
          <div className={c('step', 'reveal')}>
            <div className={c('stepNum', 'stepNumFirst')}>1</div>
            <h4>Vous créez votre espace</h4>
            <p>Inscription rapide, accès immédiat à votre tableau de bord employeur.</p>
          </div>
          <div className={c('step', 'reveal')}>
            <div className={c('stepNum')}>2</div>
            <h4>Vous partagez le code</h4>
            <p>Un code d'accès unique à transmettre par SMS, e-mail ou affiché en interne.</p>
          </div>
          <div className={c('step', 'reveal')}>
            <div className={c('stepNum')}>3</div>
            <h4>Ils activent leur compte</h4>
            <p>Téléchargement de l'app, saisie du code, accès immédiat en moins de 2 minutes.</p>
          </div>
          <div className={c('step', 'reveal')}>
            <div className={c('stepNum', 'stepNumGift')}>🎁</div>
            <h4>L'accès reste actif après la saison</h4>
            <p>Vos saisonniers gardent leurs avantages même après la fin de contrat — ils gardent un lien fort avec votre établissement.</p>
            <span className={styles.stepTag}>✓ Fidélisation entre les saisons</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection({ styles, c }) {
  return (
    <section className={styles.benefits}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <span className={c('sectionTag', 'reveal')}>Pourquoi Behandy</span>
          <h2 className={c('sectionTitle', 'reveal')}>Tout ce que vous gagnez, sans rien gérer.</h2>
        </div>
        <div className={styles.benefitsGrid}>
          {[
            { icon: '🔁', title: 'Fidélisation saison après saison', text: 'Des saisonniers valorisés reviennent davantage. Behandy crée ce lien entre deux saisons.' },
            { icon: '⭐', title: 'Marque employeur différenciante', text: "Proposez un avantage que vos concurrents n'offrent pas. Sortez du lot dès l'offre d'emploi." },
            { icon: '💸', title: "Pouvoir d'achat sans toucher aux salaires", text: "L'équivalent de plusieurs centaines d'euros d'avantages par saisonnier, sans impact sur votre masse salariale." },
            { icon: '😊', title: 'Expérience employé améliorée', text: 'Des équipes qui profitent de leur saison sont plus engagées et plus agréables à manager.' },
          ].map((b, i) => (
            <div key={b.icon} className={c('benefitCard', 'reveal')}>
              <div className={styles.benefitIcon}>{b.icon}</div>
              <div><h3>{b.title}</h3><p>{b.text}</p></div>
            </div>
          ))}
          <div className={c('benefitCard', 'benefitCardSpan2', 'reveal')}>
            <div className={styles.benefitIcon}>🚀</div>
            <div>
              <h3>Zéro complexité, zéro gestion</h3>
              <p>Behandy fonctionne en totale autonomie. Vous activez une fois, Behandy fait le reste. Aucune réunion CSE, aucun bon à commander, aucune administration. Juste un avantage qui tourne tout seul, toute la saison.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialSection({ styles, c }) {
  return (
    <section className={styles.testimonial}>
      <div className={styles.container}>
        <div className={c('testimonialBox', 'reveal')}>
          <span className={styles.stars}>★★★★★</span>
          <span className={styles.quoteMark}>"</span>
          <blockquote>
            Avec Behandy, nous pouvons proposer à nos salariés des offres de loisirs attractives pour toutes et tous. La plateforme est non seulement accessible et facile d'utilisation, mais elle offre aussi une diversité d'options qui permet de choisir.
          </blockquote>
          <div className={styles.testimonialMeta}>
            <div className={styles.testimonialAuthor}>
              <div className={styles.authorAv}>D</div>
              <div>
                <div className={styles.authorName}>Damien Ponsot</div>
                <div className={styles.authorRole}>DRH — VVF</div>
              </div>
            </div>
            <span className={styles.testimonialBadge}>✓ Client vérifié</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function VisionSection({ styles, c }) {
  return (
    <section className={styles.vision}>
      <div className={styles.container}>
        <span className={c('sectionTag', 'reveal')}>Notre vision</span>
        <h2 className={c('sectionTitle', 'reveal')}>Les saisonniers font tourner l'industrie du tourisme. Ils méritent mieux.</h2>
        <p className={c('sectionSub', 'reveal')}>Behandy existe pour transformer un contrat court en expérience valorisante. Pour les saisonniers. Pour vous.</p>
        <div className={c('visionPills', 'reveal')}>
          {[
            { text: 'Valoriser chaque saisonnier', dotClass: 'dot' },
            { text: 'Simplifier la vie des employeurs', dotClass: 'dotYellow' },
            { text: 'Créer des liens durables', dotClass: 'dot' },
            { text: 'Réinventer la saison', dotClass: 'dotYellow' },
          ].map((p, i) => (
            <div key={p.text} className={styles.visionPill}>
              <span className={styles[p.dotClass]} />
              {p.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection({ styles, c, simCount, setSimCount, simPlanType, setSimPlanType, currentTier, monthly, seasonTotal, effectiveRate, perMonthSeason, fmt, TIERS, baseRate, permanentRate, permanentMonthly, permanentAnnual, scrollToCta }) {
  const isDemanded = !currentTier?.price;
  const isSeasonal = simPlanType === 'seasonal';
  const displayRate = isSeasonal ? baseRate : permanentRate;

  return (
    <section className={styles.pricing} id="tarif">
      <div className={styles.container}>
        <div className={styles.sectionHeader} style={{ textAlign: 'center', marginBottom: 48 }}>
          <span className={c('sectionTag', 'reveal')}>Tarification</span>
          <h2 className={c('sectionTitle', 'reveal')}>Simple, transparent, sans surprise.</h2>
          <p className={c('sectionSub', 'reveal')} style={{ margin: '0 auto' }}>Deux formules selon votre activité. Zéro engagement annuel bloquant.</p>
        </div>

        {/* Toggle plan type */}
        <div className={c('planTypeToggle', 'reveal')}>
          <button
            className={c('planTypeBtn', isSeasonal ? 'planTypeBtnActive' : null)}
            onClick={() => setSimPlanType('seasonal')}
          >
            🌿 Saisonnier
          </button>
          <button
            className={c('planTypeBtn', !isSeasonal ? 'planTypeBtnActivePerm' : null)}
            onClick={() => setSimPlanType('permanent')}
          >
            ♾️ Permanent
          </button>
        </div>

        <div className={c('pricingAnchor', 'reveal')}>
          <div className={styles.pricingBigPrice}>
            <span style={{ color: isSeasonal ? 'var(--teal)' : '#6076E7' }}>{displayRate.toFixed(2).replace('.', ',')}€</span>
            <span style={{ fontSize: '0.45em', color: 'var(--muted)', letterSpacing: -1 }}> / {isSeasonal ? 'saisonnier' : 'employé'} / mois</span>
          </div>
          <p className={styles.pricingBigLabel}>
            {isSeasonal
              ? <>À partir de <strong>{displayRate.toFixed(2).replace('.', ',')}€ par mois</strong>, dégressif selon le volume</>
              : <>Un tarif fixe de <strong>{displayRate.toFixed(2).replace('.', ',')}€ par mois</strong>, résiliable après 3 mois</>
            }
          </p>
        </div>

        {isSeasonal ? (
          <div className={styles.pricingSeasonalLayout}>
            <div className={c('pricingSeasonCard', 'pricingSeasonCardFeatured')}>
              <span className={styles.seasonBadge}><span className={styles.seasonDot} />Plan Saisonnier</span>
              <h3>Durée fixe · 3 mois</h3>
              <p className={styles.pricingSeasonDates}>Idéal pour les établissements à activité saisonnière</p>
              <div className={styles.pricingIncludes}>
                <div className={styles.pricingIncludeItem}><span>✓</span> Catalogue d'avantages inclus</div>
                <div className={styles.pricingIncludeItem}><span>✓</span> Activation en 3 minutes via code</div>
                <div className={styles.pricingIncludeItem}><span>✓</span> Expire automatiquement en fin de période</div>
                <div className={styles.pricingIncludeItem}><span>✓</span> Accès conservé par le saisonnier après le contrat</div>
                <div className={styles.pricingIncludeItem}><span style={{ color: 'var(--yellow-dark)' }}>!</span> Durée fixe, non résiliable</div>
                <div className={styles.pricingIncludeItem}><span>✓</span> Tarif dégressif dès 20 sièges</div>
              </div>
              <div className={styles.pricingTierPacks}>
                {[
                  { label: '1–19', rate: TIERS[0].price },
                  { label: '20–49', rate: TIERS[1].price },
                  { label: '50–99', rate: TIERS[2].price },
                  { label: '100–199', rate: TIERS[3].price },
                  { label: '200+', rate: TIERS[4].price },
                ].map(t => (
                  <div key={t.label} className={styles.pricingTierPack}>
                    <span className={styles.pricingTierPackLabel}>{t.label}</span>
                    <span className={styles.pricingTierPackRate}>{t.rate.toFixed(2).replace('.', ',')}€</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={c('pricingSeasonCard', 'pricingSeasonCardDark')}>
              <span className={c('seasonBadge', 'seasonBadgeCustom')}><span className={styles.seasonDot} style={{ background: 'var(--yellow)' }} />Sur-mesure</span>
              <h3>Besoins spécifiques ?</h3>
              <p className={styles.pricingSeasonDates}>Plusieurs établissements, saison atypique, volume important</p>
              <div className={styles.pricingIncludes}>
                {["Offre adaptée à votre situation", "Accompagnement dédié", "Réponse sous 24h"].map(item => (
                  <div key={item} className={styles.pricingIncludeItem}><span>✓</span> {item}</div>
                ))}
              </div>
              <a href="#cta" className={styles.btnCustom} onClick={scrollToCta}>Nous contacter →</a>
            </div>
          </div>
        ) : (
          <div className={styles.pricingPermanentWrap}>
            <div className={styles.pricingPermanentCard}>
              <span className={styles.seasonBadgePerm}>
                <span className={styles.seasonDot} style={{ background: '#6076E7' }} />Plan Permanent
              </span>
              <div className={styles.permPriceRow}>
                <span className={styles.permPrice}>{permanentRate.toFixed(2).replace('.', ',')}€</span>
                <span className={styles.permPriceUnit}>/siège/mois</span>
              </div>
              <p className={styles.pricingSeasonDates}>Idéal pour les établissements avec du personnel toute l'année</p>
              <div className={styles.pricingIncludes}>
                {[
                  "Renouvellement automatique mensuel",
                  "Résiliable après 3 mois d'engagement",
                  "Ajout de sièges à tout moment",
                  "Catalogue d'avantages inclus",
                  "Activation en 3 minutes",
                ].map(item => (
                  <div key={item} className={styles.pricingIncludeItem}>
                    <span style={{ color: '#6076E7' }}>✓</span> {item}
                  </div>
                ))}
              </div>
              <a href="#cta" className={styles.btnPerm} onClick={scrollToCta}>
                Activer le plan permanent →
              </a>
            </div>
          </div>
        )}

        <div className={c('pricingSimulator', 'reveal')}>
          <div className={styles.simLabel}>Estimez votre budget</div>
          {isSeasonal ? (
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 20, fontWeight: 300 }}>
              Le tarif saisonnier est dégressif selon le volume. Entrez votre effectif pour voir votre prix exact.
            </p>
          ) : (
            <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 20, fontWeight: 300 }}>
              Tarif fixe à {permanentRate.toFixed(2).replace('.', ',')}€/siège/mois, résiliable après 3 mois.
            </p>
          )}
          <div className={styles.simInputRow}>
            <div className={styles.simInputWrap}>
              <button type="button" className={styles.simBtn} onClick={() => setSimCount(n => Math.max(1, n - 1))}>−</button>
              <input
                className={styles.simInput}
                type="number" min="1" max="500"
                value={simCount}
                onChange={e => setSimCount(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
              />
              <button type="button" className={styles.simBtn} onClick={() => setSimCount(n => Math.min(500, n + 1))}>+</button>
            </div>
            <input
              className={styles.simSlider}
              type="range" min="1" max="200"
              value={Math.min(simCount, 200)}
              onChange={e => setSimCount(parseInt(e.target.value))}
            />
            <span style={{ fontSize: 14, color: 'var(--muted)', whiteSpace: 'nowrap' }}>employés</span>
          </div>

          {isSeasonal ? (
            <>
              <div className={styles.simResults}>
                <div className={styles.simResultCard}>
                  <div className={styles.simResultTitle}>Mensuel</div>
                  <div className={styles.simResultPrice}>
                    {isDemanded ? 'Sur devis' : fmt(Math.round((monthly ?? 0) * 100) / 100)}
                    {!isDemanded && <span className={styles.simUnit}> /mois</span>}
                  </div>
                  <div className={styles.simResultDetail}>
                    {isDemanded
                      ? <>{simCount} saisonniers · <strong>contactez-nous</strong></>
                      : <>{simCount} saisonnier{simCount > 1 ? 's' : ''} · soit <strong>{(effectiveRate ?? 0).toFixed(2).replace('.', ',')}€/mois</strong> en moyenne</>
                    }
                  </div>
                </div>
                <div className={c('simResultCard', 'simResultCardHighlight')}>
                  <div className={styles.simResultTitle}>Saison entière (3 mois)</div>
                  <div className={styles.simResultPrice}>
                    {isDemanded ? 'Sur devis' : fmt(Math.round(seasonTotal ?? 0))}
                    {!isDemanded && <span className={styles.simUnit}> /saison</span>}
                  </div>
                  <div className={styles.simResultDetail}>
                    {isDemanded
                      ? 'Tarif personnalisé selon volume'
                      : `Soit ${(perMonthSeason ?? 0).toFixed(2).replace('.', ',')}€/mois/saisonnier`
                    }
                  </div>
                </div>
              </div>
              <div className={styles.simTierInfo}>
                <span className={styles.simTierLabel}>Palier :</span>
                <div className={styles.simTierPills}>
                  {TIERS.map(tier => (
                    <span key={tier.label} className={c('simTierPill', currentTier === tier ? 'simTierPillActive' : null)}>
                      {tier.label}
                    </span>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.simResults}>
              <div className={styles.simResultCard}>
                <div className={styles.simResultTitle}>Mensuel</div>
                <div className={styles.simResultPrice} style={{ color: '#6076E7' }}>
                  {fmt(Math.round(permanentMonthly * 100) / 100)}
                  <span className={styles.simUnit}> /mois</span>
                </div>
                <div className={styles.simResultDetail}>
                  {simCount} employé{simCount > 1 ? 's' : ''} · <strong>{permanentRate.toFixed(2).replace('.', ',')}€</strong> /siège/mois
                </div>
              </div>
              <div className={c('simResultCard', 'simResultCardPerm')}>
                <div className={styles.simResultTitle}>Annuel</div>
                <div className={styles.simResultPrice} style={{ color: '#6076E7' }}>
                  {fmt(Math.round(permanentAnnual))}
                  <span className={styles.simUnit}> /an</span>
                </div>
                <div className={styles.simResultDetail}>
                  {permanentRate.toFixed(2).replace('.', ',')}€ × {simCount} × 12 mois
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const FAQ_ITEMS = [
  { q: 'Est-ce compatible avec les contrats saisonniers très courts ?', a: "Oui, dès le premier jour de contrat. Vos saisonniers reçoivent un code d'accès à activer en moins de 2 minutes. Même pour un CDD d'une semaine, l'activation est instantanée." },
  { q: 'Mes saisonniers ont-ils besoin d\'un smartphone ?', a: 'Oui, Behandy est une application mobile disponible sur iOS et Android, en téléchargement gratuit. Elle est conçue pour être légère et rapide, même avec une connexion limitée.' },
  { q: 'Que se passe-t-il à la fin du contrat de mon saisonnier ?', a: "C'est là notre différence clé : vos saisonniers conservent l'accès à l'application après la fin de leur contrat. Ce geste fort crée un lien durable avec votre établissement et les incite à revenir la saison suivante." },
  { q: 'Combien de temps prend la mise en place ?', a: "Environ 3 minutes. Vous créez votre espace employeur, vous obtenez un code d'accès, et vous le transmettez à vos équipes par SMS, email ou affiché en salle de pause. C'est tout." },
  { q: 'Comment sont sélectionnés les partenaires et les offres ?', a: "Behandy négocie directement avec des enseignes locales et nationales — restaurants, loisirs, services du quotidien, séjours. Le catalogue est mis à jour en continu. Vous n'avez rien à gérer de ce côté." },
];

function FaqSection({ styles, c, openFaq, setOpenFaq }) {
  return (
    <section className={styles.faq} id="faq">
      <div className={styles.container}>
        <div className={styles.sectionHeader} style={{ textAlign: 'center', marginBottom: 48 }}>
          <span className={c('sectionTag', 'reveal')}>FAQ</span>
          <h2 className={c('sectionTitle', 'reveal')}>Questions fréquentes.</h2>
        </div>
        <div className={c('faqList', 'reveal')}>
          {FAQ_ITEMS.map((item, i) => (
            <div key={item.q} className={styles.faqItem}>
              <button className={styles.faqQ} onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                {item.q}
                <span className={c('faqIcon', openFaq === i ? 'faqIconOpen' : null)}>+</span>
              </button>
              <div className={c('faqA', openFaq === i ? 'faqAOpen' : null)}>{item.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaSection({ styles, c, formState, handleFormSubmit }) {
  return (
    <section className={styles.ctaFinal} id="cta">
      <div className={styles.container}>
        <h2 className={c('sectionTitle', 'reveal')}>Vous êtes intéressé ?</h2>
        <p className={styles.reveal}>Laissez-nous vos coordonnées — on vous contacte personnellement pour vous présenter Behandy et répondre à vos questions.</p>

        {formState === 'success' ? (
          <div className={styles.formSuccess}>
            <div className={styles.successIcon}>✓</div>
            <h3>Message envoyé !</h3>
            <p>Merci ! On vous contactera dans les 24h pour vous présenter Behandy.</p>
          </div>
        ) : (
          <form className={c('ctaForm', 'reveal')} onSubmit={handleFormSubmit}>
<input type="text" name="entreprise" placeholder="Nom de votre établissement *" required autoComplete="organization" aria-label="Nom de votre établissement" />
            <input type="email" name="email" placeholder="Votre adresse email *" required autoComplete="email" aria-label="Votre adresse email" />
            <input type="tel" name="telephone" placeholder="Votre numéro de téléphone *" required autoComplete="tel" aria-label="Votre numéro de téléphone" />
            <button type="submit" className={styles.btnDark} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }} disabled={formState === 'loading'}>
              {formState === 'loading' ? 'Envoi en cours…' : formState === 'error' ? 'Erreur — réessayez' : 'Être recontacté par Behandy'}
              {formState === 'idle' && (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h12M12 6l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          </form>
        )}
        <div className={c('ctaNote', 'reveal')}>Sans engagement · Réponse sous 24h · Zéro gestion</div>
      </div>
    </section>
  );
}

function FooterSection({ styles, c }) {
  return (
    <footer className={styles.footer}>
      <div className={styles.footerGrid}>
        <div className={styles.footerBrand}>
          {/* <img src={logoBlanc} alt="Behandy" /> */}
          <span className={styles.footerLogoText}>Behandy</span>
          <p>Le CSE pensé pour les saisonniers. Des avantages concrets, une activation en 3 minutes, zéro gestion.</p>
          <div className={styles.footerSocials}>
            <a href="https://www.instagram.com/behandy.fr" target="_blank" rel="noopener noreferrer" className={styles.footerSocial} aria-label="Instagram">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
            </a>
            <a href="https://www.linkedin.com/company/behandy" target="_blank" rel="noopener noreferrer" className={styles.footerSocial} aria-label="LinkedIn">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            </a>
          </div>
        </div>
        <div className={styles.footerCol}>
          <h4>Navigation</h4>
          <ul>
            <li><a href="#probleme">Le problème</a></li>
            <li><a href="#solution">La solution</a></li>
            <li><a href="#comment">Comment ça marche</a></li>
            <li><a href="#tarif">Tarif</a></li>
            <li><a href="#faq">FAQ</a></li>
          </ul>
        </div>
        <div className={styles.footerCol}>
          <h4>Contact</h4>
          <div className={styles.footerContactItem}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,12 2,6" /></svg>
            <a href="mailto:thibault.ricciardi@behandy.fr">thibault.ricciardi@behandy.fr</a>
          </div>
          <div className={styles.footerContactItem}>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 8.81 19.79 19.79 0 01.08 2.2 2 2 0 012.06 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>
            <a href="tel:0635168218">06 35 16 82 18</a>
          </div>
        </div>
        <div className={styles.footerCol}>
          <h4>L'application</h4>
          <div className={styles.footerAppBadges}>
            <a href="#" className={styles.footerBadge}>
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>
              App Store
            </a>
            <a href="#" className={styles.footerBadge}>
              <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M3.18 23.76c.3.17.64.22.98.15l12.08-12.08L12.77 8.4 3.18 23.76zm17.1-10.93c.46-.4.72-.96.72-1.58s-.26-1.18-.72-1.57L18.1 8.2l-3.46 3.45 3.46 3.46 1.18-.85-.01-.03zM2.28.22C1.96.58 1.8 1.1 1.8 1.74v20.5c0 .64.16 1.16.48 1.52l.08.08L14.11 12.1v-.23L2.36.14 2.28.22zm10.5 10.7l3.32-3.32-9.9-5.72c-.35-.2-.7-.28-1.02-.23L14.78 10.92z" /></svg>
              Google Play
            </a>
          </div>
          <ul style={{ marginTop: 20 }}>
            <li><a href="/mentions-legales">Mentions légales</a></li>
          </ul>
        </div>
      </div>
      <div className={styles.footerBottom}>
        <span>© 2026 Behandy · Le CSE des saisonniers</span>
        <span>Fait avec ❤️ pour les saisonniers de France</span>
      </div>
    </footer>
  );
}
